import { API_BASE_URL, apiFetch } from '../api/client';
import {
  login as loginApi,
  logout as logoutApi,
  me as meApi,
  registerMember,
  resendMemberRegistrationCode,
  verifyMemberRegistrationCode
} from '../api/auth';
import {
  changeMemberPassword as changeMemberPasswordApi,
  getAdminMemberProfile as getAdminMemberProfileApi,
  getMemberProfile as getMemberProfileApi,
  getMemberProfileSchema as getMemberProfileSchemaApi,
  getPublicMembers as getPublicMembersApi,
  saveAdminMemberProfile as saveAdminMemberProfileApi,
  saveMemberProfile as saveMemberProfileApi
} from '../api/member';
import {
  activateMember as activateMemberApi,
  approveMember as approveMemberApi,
  deactivateMember as deactivateMemberApi,
  getMembers as getMembersApi
} from '../api/admin';
import { getPublicSiteSettings, getPublicStats as getPublicStatsApi } from '../api/site';
import {
  createAdminEvent as createAdminEventApi,
  deleteAdminEvent as deleteAdminEventApi,
  getAdminEvents as getAdminEventsApi,
  getPublicEvents as getPublicEventsApi,
  updateAdminEvent as updateAdminEventApi
} from '../api/events';
import {
  getPublicAlumniContent as getPublicAlumniContentApi,
  saveAdminAlumniContent as saveAdminAlumniContentApi,
  uploadAdminAlumniBanner as uploadAdminAlumniBannerApi
} from '../api/alumniContent';

const SESSION_KEY = 'alumni_session';
const HOMEPAGE_KEY = 'alumni_homepage_content';
const ABOUT_KEY = 'alumni_about_content';
const CONTACT_KEY = 'alumni_contact_content';
const SITE_KEY = 'alumni_site_content';
const ALUMNI_KEY = 'alumni_content';

const DEFAULT_HOMEPAGE_CONTENT = {
  heroEyebrow: 'Welcome back, Alumni Member',
  heroTitle: 'Lead the next chapter of the Chatar story.',
  heroLead:
    'Stay connected with classmates, discover mentorship circles, and keep the momentum alive through events, funding, and collaboration.',
  slides: [
    {
      title: 'Reconnect with the alumni who shaped your story.',
      subtitle: 'Join a growing network of mentors, founders, and change-makers.',
      tag: 'Alumni Spotlight',
      cta: 'Explore stories',
      accent: '#0B2948',
      glow: '#D4A017'
    },
    {
      title: 'Give back to the next generation of innovators.',
      subtitle: 'Support scholarships, labs, and student-led initiatives.',
      tag: 'Impact Fund',
      cta: 'See impact',
      accent: '#0B2948',
      glow: '#D4A017'
    },
    {
      title: 'Plan your next visit to campus.',
      subtitle: 'Homecoming, reunions, and regional chapters await you.',
      tag: 'Upcoming Events',
      cta: 'View calendar',
      accent: '#0B2948',
      glow: '#D4A017'
    }
  ],
  eventsHeading: 'Upcoming alumni experiences',
  eventsSubheading: 'Plan meetups, reunions, and impact projects across the globe.',
  events: [
    {
      title: 'Boston Homecoming Weekend',
      description: 'Jan 18-20 · Panels, campus tour, and class dinners.',
      action: 'Reserve seat'
    },
    {
      title: 'Virtual Career Summit',
      description: 'Feb 12 · 20+ speakers on leadership and innovation.',
      action: 'Join online'
    },
    {
      title: 'Singapore Chapter Mixer',
      description: 'Mar 02 · Expand your network in Southeast Asia.',
      action: 'RSVP'
    }
  ],
  newsletterHeading: 'Stay connected every month',
  newsletterText: 'Get alumni stories, opportunities, and events in your inbox.'
};

const DEFAULT_ABOUT_CONTENT = {
  heroEyebrow: 'About us',
  heroTitle: 'We connect generations of Chatar alumni worldwide.',
  heroText:
    'Our network helps alumni stay close to campus, grow careers, and build lifelong impact through mentoring, events, and community-led giving.',
  missionTitle: 'Our mission',
  missionText:
    'To strengthen alumni relationships and empower every graduate to give back, mentor, and lead.',
  values: [
    {
      title: 'Community first',
      text: 'We design every program to foster trust, belonging, and support.'
    },
    {
      title: 'Career momentum',
      text: 'We build mentorship pipelines and hiring connections worldwide.'
    },
    {
      title: 'Giving with purpose',
      text: 'We fund scholarships and innovation for the next generation.'
    }
  ],
  storyTitle: 'Our story',
  storyText:
    'From small reunions to a global alumni network, Chatar graduates have always shown up for one another. Today we host regional chapters, industry councils, and cross-campus initiatives that keep the alumni voice strong and united.'
};

const DEFAULT_CONTACT_CONTENT = {
  heroEyebrow: 'Contact us',
  heroTitle: 'Let’s keep your alumni story moving.',
  heroLead:
    'Reach out for partnership, mentorship, or event collaboration. Our team is ready to help you stay connected and make an impact.',
  officeTitle: 'Chatar Alumni Office',
  officeAddress: '97 Cambridge Avenue, Boston, MA',
  officeEmail: 'alumni@chatar.org',
  officePhone: '+1 (617) 555-0142',
  officeBadge: 'Response within 24 hours',
  mapEmbedUrl: 'https://maps.google.com/maps?q=Boston%20MA&t=&z=12&ie=UTF8&iwloc=&output=embed',
  formTitle: 'Send us a message',
  formText: 'Tell us how we can help. We will reply as soon as possible.',
  highlights: [
    {
      title: 'Alumni Relations',
      text: 'Guidance on events, reunions, and alumni programming.'
    },
    {
      title: 'Giving & Partnerships',
      text: 'Support scholarships, labs, and community initiatives.'
    },
    {
      title: 'Career Connections',
      text: 'Mentorship, hiring, and networking opportunities.'
    }
  ]
};

const DEFAULT_SITE_CONTENT = {
  companyName: 'Chatar Alumni Network',
  brandTitle: 'Chatar Alumni',
  brandSubtitle: 'Connect · Mentor · Build',
  topHeaderAddress: '97 Cambridge Avenue, Boston, MA',
  topHeaderEmail: 'alumni@chatar.org',
  topHeaderPhone: '+1 (617) 555-0142',
  linkedinUrl: 'https://www.linkedin.com',
  facebookUrl: 'https://www.facebook.com',
  instagramUrl: 'https://www.instagram.com',
  whatsappUrl: 'https://wa.me/16175550142',
  logoUrl: ''
};

const DEFAULT_ALUMNI_CONTENT = {
  alumniName: 'SFACO CTG',
  alumniDescription:
    'Stay connected with classmates, discover mentorship circles, and keep the momentum alive through events, funding, and collaboration.',
  bannerHeading: 'Reconnect with the alumni who shaped your story.',
  banners: []
};

const DEFAULT_MEMBER_PROFILE_SCHEMA = [
  { name: 'name', label: 'Full name', type: 'text', required: true },
  { name: 'email', label: 'Email', type: 'email', required: true },
  { name: 'date_of_birth', label: 'Date of birth', type: 'date' },
  { name: 'nid_passport_number', label: 'NID / Passport number', type: 'text' },
  {
    name: 'gender',
    label: 'Gender',
    type: 'select',
    options: [
      { value: 'male', label: 'Male' },
      { value: 'female', label: 'Female' }
    ]
  },
  { name: 'whatsapp_number', label: 'WhatsApp number', type: 'tel' },
  { name: 'current_address', label: 'Current address', type: 'textarea' },
  { name: 'permanent_address', label: 'Permanent address', type: 'textarea' },
  { name: 'articled_period_from', label: 'Articled period from', type: 'date' },
  { name: 'articled_period_to', label: 'Articled period to', type: 'date' },
  { name: 'principal_supervisor_name', label: 'Principal supervisor name', type: 'text' },
  { name: 'icab_registration_no', label: 'ICAB registration no', type: 'text' },
  { name: 'current_organization', label: 'Current organization', type: 'text' },
  { name: 'designation', label: 'Designation', type: 'text' },
  { name: 'ca_status', label: 'CA status', type: 'text' },
  { name: 'profile_photo', label: 'Profile photo', type: 'file' }
];

function loadHomePageContent() {
  const raw = localStorage.getItem(HOMEPAGE_KEY);
  if (!raw) {
    localStorage.setItem(HOMEPAGE_KEY, JSON.stringify(DEFAULT_HOMEPAGE_CONTENT));
    return { ...DEFAULT_HOMEPAGE_CONTENT };
  }

  const parsed = JSON.parse(raw);
  return {
    ...DEFAULT_HOMEPAGE_CONTENT,
    ...parsed,
    slides: parsed?.slides?.length ? parsed.slides : DEFAULT_HOMEPAGE_CONTENT.slides,
    events: parsed?.events?.length ? parsed.events : DEFAULT_HOMEPAGE_CONTENT.events
  };
}

function saveHomePageContent(content) {
  localStorage.setItem(HOMEPAGE_KEY, JSON.stringify(content));
}

function loadAboutContent() {
  const raw = localStorage.getItem(ABOUT_KEY);
  if (!raw) {
    localStorage.setItem(ABOUT_KEY, JSON.stringify(DEFAULT_ABOUT_CONTENT));
    return { ...DEFAULT_ABOUT_CONTENT };
  }

  const parsed = JSON.parse(raw);
  return {
    ...DEFAULT_ABOUT_CONTENT,
    ...parsed,
    values: parsed?.values?.length ? parsed.values : DEFAULT_ABOUT_CONTENT.values
  };
}

function saveAboutContent(content) {
  localStorage.setItem(ABOUT_KEY, JSON.stringify(content));
}

function loadContactContent() {
  const raw = localStorage.getItem(CONTACT_KEY);
  if (!raw) {
    localStorage.setItem(CONTACT_KEY, JSON.stringify(DEFAULT_CONTACT_CONTENT));
    return { ...DEFAULT_CONTACT_CONTENT };
  }

  const parsed = JSON.parse(raw);
  return {
    ...DEFAULT_CONTACT_CONTENT,
    ...parsed,
    highlights: parsed?.highlights?.length ? parsed.highlights : DEFAULT_CONTACT_CONTENT.highlights
  };
}

function saveContactContent(content) {
  localStorage.setItem(CONTACT_KEY, JSON.stringify(content));
}

function loadSiteContent() {
  const raw = localStorage.getItem(SITE_KEY);
  if (!raw) {
    localStorage.setItem(SITE_KEY, JSON.stringify(DEFAULT_SITE_CONTENT));
    return { ...DEFAULT_SITE_CONTENT };
  }
  return {
    ...DEFAULT_SITE_CONTENT,
    ...JSON.parse(raw)
  };
}

function saveSiteContent(content) {
  localStorage.setItem(SITE_KEY, JSON.stringify(content));
}

function loadAlumniContent() {
  const raw = localStorage.getItem(ALUMNI_KEY);
  if (!raw) {
    localStorage.setItem(ALUMNI_KEY, JSON.stringify(DEFAULT_ALUMNI_CONTENT));
    return { ...DEFAULT_ALUMNI_CONTENT };
  }
  return {
    ...DEFAULT_ALUMNI_CONTENT,
    ...JSON.parse(raw)
  };
}

function saveAlumniContent(content) {
  localStorage.setItem(ALUMNI_KEY, JSON.stringify(content));
}

function saveSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

function clearSession() {
  localStorage.removeItem(SESSION_KEY);
}

function scrubUser(user) {
  const { password, ...safeUser } = user;
  return safeUser;
}

function throwError(message) {
  const err = new Error(message);
  err.isHandled = true;
  throw err;
}

function isBackendReady(error) {
  const status = error?.response?.status || error?.status;
  if (!status) return false;

  const data = error?.response?.data || error?.data;
  const message = (
    typeof data === 'string'
      ? data
      : data?.message || data?.error || ''
  )
    .toString()
    .toLowerCase();

  // If backend is up but auth routes are missing, use local fallback mode.
  if (status === 404 && (message.includes('route') || message.includes('not found'))) {
    return false;
  }

  return status < 500;
}

function getMessage(error, fallback) {
  return error?.response?.data?.message || error?.data?.message || error?.message || fallback;
}

function normalizeAssetUrl(rawUrl) {
  const value = String(rawUrl || '').trim();
  if (!value) return '';

  let apiOrigin = '';
  try {
    apiOrigin = new URL(API_BASE_URL).origin;
  } catch (error) {
    apiOrigin = '';
  }

  if (value.startsWith('/')) {
    return apiOrigin ? `${apiOrigin}${value}` : value;
  }

  if (value.startsWith('uploads/')) {
    return apiOrigin ? `${apiOrigin}/${value}` : `/${value}`;
  }

  if (!/^https?:\/\//i.test(value)) {
    return apiOrigin ? `${apiOrigin}/${value.replace(/^\/+/, '')}` : value;
  }

  try {
    const parsed = new URL(value);
    if (parsed.hostname === 'localhost' && apiOrigin) {
      const apiParsed = new URL(apiOrigin);
      parsed.protocol = apiParsed.protocol;
      parsed.hostname = apiParsed.hostname;
      parsed.port = apiParsed.port;
      return parsed.toString();
    }
  } catch (error) {
    return value;
  }

  return value;
}

function extractObject(payload) {
  if (!payload || typeof payload !== 'object') return {};
  if (Array.isArray(payload)) return {};
  if (payload.settings && typeof payload.settings === 'object') return payload.settings;
  if (payload.content && typeof payload.content === 'object') return payload.content;
  if (payload.profile && typeof payload.profile === 'object') return payload.profile;
  if (payload.member && typeof payload.member === 'object') return payload.member;
  if (payload.user && typeof payload.user === 'object') return payload.user;
  if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
    return extractObject(payload.data);
  }
  return payload;
}

function toBoolean(value, fallback = false) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const v = value.toLowerCase();
    if (v === '1' || v === 'true' || v === 'yes') return true;
    if (v === '0' || v === 'false' || v === 'no') return false;
  }
  return fallback;
}

function normalizeUser(user) {
  if (!user) return null;
  const rawApproval =
    user.approved ??
    user.is_approved ??
    user.approval_status ??
    user.status;
  const rawActive = user.active ?? user.is_active ?? user.status;
  const statusText = String(user.approval_status ?? user.status ?? '').toLowerCase();
  const inferredApproved =
    statusText === 'approved' || statusText === 'active' || statusText === 'activated';
  const inferredPending =
    statusText === 'pending' || statusText === 'waiting' || statusText === 'awaiting_approval';

  return {
    ...user,
    fullName: user.fullName || user.full_name || user.name || '',
    profilePhotoUrl: normalizeAssetUrl(
      user.profilePhotoUrl ||
      user.profile_photo_url ||
      user.profile_photo ||
      user.photo_url ||
      user.avatar_url ||
      ''
    ),
    articledPeriod: user.articledPeriod || user.articled_period || '',
    emailVerified: toBoolean(user.emailVerified ?? user.email_verified, true),
    approved: inferredPending ? false : toBoolean(rawApproval, inferredApproved),
    active: toBoolean(rawActive, true),
    showEmail: toBoolean(user.showEmail ?? user.show_email, true),
    showPhone: toBoolean(user.showPhone ?? user.show_phone, false),
    role: user.role || 'member'
  };
}

function normalizeSchemaOption(option) {
  if (option && typeof option === 'object') {
    return {
      value: option.value ?? option.id ?? option.key ?? option.label ?? '',
      label: option.label ?? option.name ?? option.value ?? option.id ?? ''
    };
  }
  return { value: option ?? '', label: option ?? '' };
}

function normalizeSchemaField(field) {
  const name = field?.name || field?.key || field?.field_name || '';
  const inferredType =
    name === 'articled_period_from' || name === 'articled_period_to'
      ? 'month'
      : field?.type || field?.input_type || field?.field_type || 'text';
  const resolvedType = name === 'gender' ? 'select' : inferredType;
  const options = name === 'gender'
    ? [
      { value: 'male', label: 'Male' },
      { value: 'female', label: 'Female' }
    ]
    : Array.isArray(field?.options)
      ? field.options.map(normalizeSchemaOption)
      : [];
  return {
    name,
    label: field?.label || field?.title || field?.name || '',
    type: resolvedType,
    placeholder: field?.placeholder || '',
    required: !!field?.required,
    options,
    accept: field?.accept || '',
    helperText: field?.helper_text || field?.description || ''
  };
}

function normalizeGenderValue(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'm') return 'male';
  if (raw === 'f') return 'female';
  if (raw === 'male' || raw === 'female') return raw;
  return String(value || '').trim();
}

function normalizeMemberProfile(profile) {
  const source = extractObject(profile);
  return {
    id: source.id || source.user_id || '',
    name: source.name || source.full_name || source.fullName || '',
    email: source.email || '',
    date_of_birth: source.date_of_birth || '',
    nid_passport_number: source.nid_passport_number || '',
    gender: normalizeGenderValue(source.gender),
    whatsapp_number: source.whatsapp_number || source.phone || '',
    current_address: source.current_address || '',
    permanent_address: source.permanent_address || '',
    articled_period_from: (source.articled_period_from || '').slice(0, 7),
    articled_period_to: (source.articled_period_to || '').slice(0, 7),
    principal_supervisor_name: source.principal_supervisor_name || '',
    icab_registration_no: source.icab_registration_no || '',
    current_organization: source.current_organization || '',
    designation: source.designation || source.profession || '',
    ca_status: source.ca_status || '',
    profile_photo: null,
    profile_photo_url:
      source.profile_photo_url ||
      source.profile_photo ||
      source.photo_url ||
      source.avatar_url ||
      '',
    approved: toBoolean(source.approved ?? source.is_approved, false),
    active: toBoolean(source.active ?? source.is_active, true),
    role: source.role || 'member'
  };
}

function normalizeSiteContent(payload) {
  const source = extractObject(payload);
  return {
    companyName: source.companyName || source.company_name || DEFAULT_SITE_CONTENT.companyName,
    brandTitle: source.brandTitle || source.brand_title || DEFAULT_SITE_CONTENT.brandTitle,
    brandSubtitle: source.brandSubtitle || source.brand_subtitle || DEFAULT_SITE_CONTENT.brandSubtitle,
    topHeaderAddress: source.topHeaderAddress || source.top_header_address || DEFAULT_SITE_CONTENT.topHeaderAddress,
    topHeaderEmail: source.topHeaderEmail || source.top_header_email || DEFAULT_SITE_CONTENT.topHeaderEmail,
    topHeaderPhone: source.topHeaderPhone || source.top_header_phone || DEFAULT_SITE_CONTENT.topHeaderPhone,
    linkedinUrl: source.linkedinUrl || source.linkedin_url || DEFAULT_SITE_CONTENT.linkedinUrl,
    facebookUrl: source.facebookUrl || source.facebook_url || DEFAULT_SITE_CONTENT.facebookUrl,
    instagramUrl: source.instagramUrl || source.instagram_url || DEFAULT_SITE_CONTENT.instagramUrl,
    whatsappUrl: source.whatsappUrl || source.whatsapp_url || DEFAULT_SITE_CONTENT.whatsappUrl,
    logoUrl: normalizeAssetUrl(source.logoUrl || source.logo_url || DEFAULT_SITE_CONTENT.logoUrl)
  };
}

function normalizeAlumniContent(payload) {
  const source = extractObject(payload?.content || payload);
  const rawBanners = Array.isArray(source.banners) ? source.banners : [];
  const banners = rawBanners
    .map((banner) => ({
      imageUrl: normalizeAssetUrl(banner?.imageUrl || banner?.image_url || ''),
      heading: banner?.heading || ''
    }))
    .filter((banner) => banner.imageUrl);

  return {
    alumniName: source.alumniName || source.alumni_name || DEFAULT_ALUMNI_CONTENT.alumniName,
    alumniDescription:
      source.alumniDescription || source.alumni_description || DEFAULT_ALUMNI_CONTENT.alumniDescription,
    bannerHeading: source.bannerHeading || source.banner_heading || DEFAULT_ALUMNI_CONTENT.bannerHeading,
    banners
  };
}

function toSiteSettingsFormData(payload) {
  const formData = new FormData();
  const fields = {
    company_name: payload.companyName,
    brand_title: payload.brandTitle,
    brand_subtitle: payload.brandSubtitle,
    top_header_address: payload.topHeaderAddress,
    top_header_email: payload.topHeaderEmail,
    top_header_phone: payload.topHeaderPhone,
    linkedin_url: payload.linkedinUrl,
    facebook_url: payload.facebookUrl,
    instagram_url: payload.instagramUrl,
    whatsapp_url: payload.whatsappUrl
  };

  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, String(value));
    }
  });

  if (payload.removeLogo) {
    formData.append('remove_logo', '1');
  }

  if (typeof File !== 'undefined' && payload.logoFile instanceof File) {
    formData.append('logo', payload.logoFile);
  }

  return formData;
}

function buildProfileFormData(values, schema = DEFAULT_MEMBER_PROFILE_SCHEMA) {
  const formData = new FormData();
  const fieldNames = schema
    .map((field) => field.name)
    .filter(Boolean);
  const names = fieldNames.length ? fieldNames : Object.keys(values || {});

  names.forEach((name) => {
    const value = values?.[name];
    if (value === undefined || value === null || value === '') return;
    if (typeof File !== 'undefined' && value instanceof File) {
      formData.append(name, value);
      return;
    }
    if (typeof Blob !== 'undefined' && value instanceof Blob) {
      formData.append(name, value);
      return;
    }
    formData.append(name, String(value));
  });

  return formData;
}

async function fetchCurrentUserFromApi() {
  const data = await meApi();
  return normalizeUser(data?.user || data?.data?.user || data?.data || data);
}

function normalizeMemberRecord(row) {
  const source = row?.user ? { ...row.user, ...row } : row;
  const profile = source?.member_profile || source?.memberProfile || source?.profile || {};
  const normalized = normalizeUser(source);
  return {
    id: normalized.id || normalized.user_id || source?.id || source?.user_id,
    fullName: normalized.fullName,
    email: normalized.email || '',
    identity: normalized.identity || normalized.identity_number || '',
    articledPeriod: normalized.articledPeriod,
    phone: normalized.phone || '',
    city: normalized.city || '',
    country: normalized.country || '',
    profession: normalized.profession || '',
    designation:
      source.designation ||
      source.designation_name ||
      profile.designation ||
      profile.designation_name ||
      normalized.profession ||
      '',
    current_organization:
      source.current_organization ||
      source.currentOrganization ||
      profile.current_organization ||
      profile.currentOrganization ||
      '',
    ca_status:
      source.ca_status ||
      source.caStatus ||
      profile.ca_status ||
      profile.caStatus ||
      '',
    icab_registration_no:
      source.icab_registration_no ||
      source.icabRegistrationNo ||
      profile.icab_registration_no ||
      profile.icabRegistrationNo ||
      '',
    emailVerified: normalized.emailVerified,
    approved: normalized.approved,
    active: normalized.active,
    role: normalized.role,
    createdAt: normalized.createdAt || normalized.created_at || ''
  };
}

function normalizePublicMember(row) {
  const source = extractObject(row);
  return {
    id: source.id || source.user_id || '',
    name: source.name || source.full_name || source.fullName || '',
    currentOrganization: source.current_organization || source.currentOrganization || '',
    designation: source.designation || source.profession || '',
    profilePhotoUrl: normalizeAssetUrl(
      source.profile_photo_url ||
      source.profile_photo ||
      source.photo_url ||
      source.avatar_url ||
      ''
    )
  };
}

function normalizePublicMemberList(payload) {
  const rows = extractRows(payload);
  return rows
    .map(normalizePublicMember)
    .filter((member) => member.name)
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

function normalizePublicMembersPagination(payload, fallbackPerPage = 20) {
  const source = extractObject(payload?.pagination || payload?.meta || {});
  const perPage = Number(source.per_page ?? source.perPage ?? fallbackPerPage);
  const total = Number(source.total ?? 0);
  const lastPage = Number(source.last_page ?? source.lastPage ?? 1);
  const currentPage = Number(source.current_page ?? source.currentPage ?? 1);
  const from = Number(source.from ?? 0);
  const to = Number(source.to ?? 0);

  return {
    currentPage: Number.isFinite(currentPage) && currentPage > 0 ? currentPage : 1,
    lastPage: Number.isFinite(lastPage) && lastPage > 0 ? lastPage : 1,
    perPage: Number.isFinite(perPage) && perPage > 0 ? perPage : fallbackPerPage,
    total: Number.isFinite(total) && total >= 0 ? total : 0,
    from: Number.isFinite(from) && from >= 0 ? from : 0,
    to: Number.isFinite(to) && to >= 0 ? to : 0
  };
}

function normalizeEvent(event, index = 0) {
  const source = extractObject(event);
  const numericSort = Number(source.sort_order ?? source.sortOrder ?? index);

  return {
    id: source.id ?? source.event_id ?? `event-${index}`,
    title: source.title || source.name || '',
    dateLabel: source.date_label || source.dateLabel || source.date || '',
    description: source.description || source.details || '',
    action: source.action || source.action_label || source.cta || 'Join now',
    actionUrl: source.action_url || source.actionUrl || '',
    sortOrder: Number.isFinite(numericSort) ? numericSort : index,
    active: toBoolean(source.is_active ?? source.active, true)
  };
}

function normalizeEventList(payload, includeInactive = false) {
  const rows = extractRows(payload);
  const normalized = rows
    .map((row, index) => normalizeEvent(row, index))
    .filter((event) => event.title);

  const visible = includeInactive ? normalized : normalized.filter((event) => event.active);

  return visible.sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) {
      return a.sortOrder - b.sortOrder;
    }
    return String(a.title).localeCompare(String(b.title));
  });
}

function normalizeSingleEvent(payload) {
  if (!payload || typeof payload !== 'object') return null;

  const candidate =
    payload.event ||
    payload.item ||
    payload.data?.event ||
    payload.data?.item ||
    payload.data;

  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return null;
  }

  const normalized = normalizeEvent(candidate, 0);
  return normalized.title ? normalized : null;
}

function toEventPayload(payload) {
  return {
    title: String(payload?.title || '').trim(),
    date_label: payload?.dateLabel ? String(payload.dateLabel).trim() : null,
    description: payload?.description ? String(payload.description).trim() : null,
    action_label: payload?.action ? String(payload.action).trim() : null,
    action_url: payload?.actionUrl ? String(payload.actionUrl).trim() : null,
    sort_order: Number.isFinite(Number(payload?.sortOrder)) ? Number(payload.sortOrder) : 0,
    is_active: !!payload?.active
  };
}

function extractRows(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.events)) return payload.events;
  if (Array.isArray(payload?.members)) return payload.members;
  if (Array.isArray(payload?.users)) return payload.users;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.events?.data)) return payload.events.data;
  if (Array.isArray(payload?.members?.data)) return payload.members.data;
  if (Array.isArray(payload?.users?.data)) return payload.users.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data?.events)) return payload.data.events;
  if (Array.isArray(payload?.data?.members)) return payload.data.members;
  if (Array.isArray(payload?.data?.users)) return payload.data.users;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data?.events?.data)) return payload.data.events.data;
  if (Array.isArray(payload?.data?.members?.data)) return payload.data.members.data;
  if (Array.isArray(payload?.data?.users?.data)) return payload.data.users.data;

  // Last fallback: recursively find first array under common wrapper keys.
  const commonKeys = ['data', 'result', 'results', 'payload', 'events', 'members', 'users', 'items'];
  for (const key of commonKeys) {
    if (payload && typeof payload === 'object' && payload[key]) {
      const rows = extractRows(payload[key]);
      if (rows.length) return rows;
    }
  }
  return [];
}

export function getStoredSession() {
  const raw = localStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  const session = JSON.parse(raw);
  if (typeof session?.token === 'string' && session.token.startsWith('mock-')) {
    clearSession();
    localStorage.removeItem('token');
    return null;
  }
  return session;
}

export function logout() {
  // Best effort backend logout. Frontend session is cleared regardless of response.
  logoutApi().catch(() => {});
  clearSession();
}

export function getDefaultHomePageContent() {
  return JSON.parse(JSON.stringify(DEFAULT_HOMEPAGE_CONTENT));
}

export function getDefaultAboutContent() {
  return JSON.parse(JSON.stringify(DEFAULT_ABOUT_CONTENT));
}

export function getDefaultContactContent() {
  return JSON.parse(JSON.stringify(DEFAULT_CONTACT_CONTENT));
}

export function getDefaultSiteContent() {
  return JSON.parse(JSON.stringify(DEFAULT_SITE_CONTENT));
}

export function getDefaultAlumniContent() {
  return JSON.parse(JSON.stringify(DEFAULT_ALUMNI_CONTENT));
}

export async function getHomePageContent() {
  try {
    const res = await apiFetch('/admin/homepage-settings');
    return {
      ...getDefaultHomePageContent(),
      ...res
    };
  } catch (error) {
    if (error?.status !== 404 && isBackendReady(error)) {
      throwError(getMessage(error, 'Unable to load homepage settings.'));
    }
    return loadHomePageContent();
  }
}

export async function updateHomePageContent(payload) {
  try {
    return await apiFetch('/admin/homepage-settings', { method: 'PUT', body: payload });
  } catch (error) {
    if (error?.status !== 404 && isBackendReady(error)) {
      throwError(getMessage(error, 'Unable to update homepage settings.'));
    }
    const next = {
      ...loadHomePageContent(),
      ...payload,
      slides: payload?.slides?.length ? payload.slides : loadHomePageContent().slides,
      events: payload?.events?.length ? payload.events : loadHomePageContent().events
    };
    saveHomePageContent(next);
    return { message: 'Homepage updated.', content: next };
  }
}

export async function getAboutContent() {
  try {
    const res = await apiFetch('/admin/about-settings');
    return {
      ...getDefaultAboutContent(),
      ...res
    };
  } catch (error) {
    if (error?.status !== 404 && isBackendReady(error)) {
      throwError(getMessage(error, 'Unable to load about settings.'));
    }
    return loadAboutContent();
  }
}

export async function updateAboutContent(payload) {
  try {
    return await apiFetch('/admin/about-settings', { method: 'PUT', body: payload });
  } catch (error) {
    if (error?.status !== 404 && isBackendReady(error)) {
      throwError(getMessage(error, 'Unable to update about settings.'));
    }
    const next = {
      ...loadAboutContent(),
      ...payload,
      values: payload?.values?.length ? payload.values : loadAboutContent().values
    };
    saveAboutContent(next);
    return { message: 'About page updated.', content: next };
  }
}

export async function getContactContent() {
  try {
    const res = await apiFetch('/admin/contact-settings');
    return {
      ...getDefaultContactContent(),
      ...res
    };
  } catch (error) {
    if (error?.status !== 404 && isBackendReady(error)) {
      throwError(getMessage(error, 'Unable to load contact settings.'));
    }
    return loadContactContent();
  }
}

export async function updateContactContent(payload) {
  try {
    return await apiFetch('/admin/contact-settings', { method: 'PUT', body: payload });
  } catch (error) {
    if (error?.status !== 404 && isBackendReady(error)) {
      throwError(getMessage(error, 'Unable to update contact settings.'));
    }
    const next = {
      ...loadContactContent(),
      ...payload,
      highlights: payload?.highlights?.length ? payload.highlights : loadContactContent().highlights
    };
    saveContactContent(next);
    return { message: 'Contact page updated.', content: next };
  }
}

export async function getSiteContent() {
  try {
    const res = await getPublicSiteSettings();
    return normalizeSiteContent(res);
  } catch (error) {
    if (isBackendReady(error)) {
      throwError(getMessage(error, 'Unable to load site settings.'));
    }
    return loadSiteContent();
  }
}

export async function getPublicMemberCount() {
  try {
    const res = await getPublicStatsApi();
    const count = Number(res?.member_count ?? res?.memberCount ?? 0);
    return Number.isFinite(count) && count >= 0 ? count : 0;
  } catch (error) {
    if (isBackendReady(error)) {
      throwError(getMessage(error, 'Unable to load member count.'));
    }
    return 0;
  }
}

export async function getPublicMembers(page = 1, perPage = 20, query = '') {
  try {
    const response = await getPublicMembersApi(page, perPage, query);
    return {
      members: normalizePublicMemberList(response),
      pagination: normalizePublicMembersPagination(response, perPage)
    };
  } catch (error) {
    if (isBackendReady(error)) {
      throwError(getMessage(error, 'Unable to load members.'));
    }
    return {
      members: [],
      pagination: {
        currentPage: 1,
        lastPage: 1,
        perPage,
        total: 0,
        from: 0,
        to: 0
      }
    };
  }
}

export async function updateSiteContent(payload) {
  try {
    const res = await apiFetch('/admin/site-settings', {
      method: 'POST',
      body: toSiteSettingsFormData(payload),
      token: localStorage.getItem('token')
    });
    const normalized = normalizeSiteContent(res);
    saveSiteContent(normalized);
    return { ...res, settings: normalized };
  } catch (error) {
    if (isBackendReady(error)) {
      throwError(getMessage(error, 'Unable to update site settings.'));
    }
    const next = {
      ...loadSiteContent(),
      ...payload
    };
    saveSiteContent(next);
    return { message: 'Site settings updated.', content: next };
  }
}

export async function getAlumniContent() {
  try {
    const res = await getPublicAlumniContentApi();
    const normalized = normalizeAlumniContent(res);
    saveAlumniContent(normalized);
    return normalized;
  } catch (error) {
    if (isBackendReady(error)) {
      throwError(getMessage(error, 'Unable to load alumni content.'));
    }
    return loadAlumniContent();
  }
}

export async function updateAlumniContent(payload) {
  try {
    const res = await saveAdminAlumniContentApi({
      alumni_name: payload.alumniName,
      alumni_description: payload.alumniDescription,
      banner_heading: payload.bannerHeading,
      banners: (Array.isArray(payload.banners) ? payload.banners : [])
        .map((banner) => ({
          image_url: String(banner?.imageUrl || '').trim(),
          heading: String(banner?.heading || '').trim() || null
        }))
        .filter((banner) => banner.image_url)
    });
    const normalized = normalizeAlumniContent(res);
    saveAlumniContent(normalized);
    return { ...res, content: normalized };
  } catch (error) {
    if (isBackendReady(error)) {
      throwError(getMessage(error, 'Unable to update alumni content.'));
    }
    const next = {
      ...loadAlumniContent(),
      ...payload
    };
    saveAlumniContent(next);
    return { message: 'Alumni content updated.', content: next };
  }
}

export async function uploadAlumniBanner(file) {
  try {
    const formData = new FormData();
    formData.append('banner', file);
    const res = await uploadAdminAlumniBannerApi(formData);
    return {
      imageUrl: normalizeAssetUrl(res?.image_url || res?.imageUrl || '')
    };
  } catch (error) {
    throwError(getMessage(error, 'Unable to upload banner.'));
  }
}

export async function getPublicEvents() {
  try {
    const response = await getPublicEventsApi();
    return normalizeEventList(response);
  } catch (error) {
    if (isBackendReady(error)) {
      throwError(getMessage(error, 'Unable to load events.'));
    }
  }

  return loadHomePageContent().events.map((event, index) => normalizeEvent(event, index));
}

export async function getAdminEvents() {
  try {
    const response = await getAdminEventsApi();
    return normalizeEventList(response, true);
  } catch (error) {
    throwError(getMessage(error, 'Unable to load events.'));
  }
}

export async function createAdminEvent(payload) {
  try {
    const response = await createAdminEventApi(toEventPayload(payload));
    return normalizeSingleEvent(response);
  } catch (error) {
    throwError(getMessage(error, 'Unable to create event.'));
  }
}

export async function updateAdminEvent(eventId, payload) {
  try {
    const response = await updateAdminEventApi(eventId, toEventPayload(payload));
    return normalizeSingleEvent(response);
  } catch (error) {
    throwError(getMessage(error, 'Unable to update event.'));
  }
}

export async function deleteAdminEvent(eventId) {
  try {
    return await deleteAdminEventApi(eventId);
  } catch (error) {
    throwError(getMessage(error, 'Unable to delete event.'));
  }
}

export async function registerAlumni(payload) {
  try {
    return await registerMember(payload);
  } catch (error) {
    throwError(getMessage(error, 'Registration failed.'));
  }
}

export async function verifyEmail(email, code) {
  try {
    return await verifyMemberRegistrationCode(email, code);
  } catch (error) {
    throwError(getMessage(error, 'Email verification failed.'));
  }
}

export async function resendRegistrationCode(email) {
  try {
    return await resendMemberRegistrationCode(email);
  } catch (error) {
    throwError(getMessage(error, 'Unable to resend verification code.'));
  }
}

export async function loginWithPassword(form) {
  try {
    const res = await loginApi(form.email, form.password);
    const token =
      res?.token ||
      res?.access_token ||
      res?.accessToken ||
      res?.data?.token ||
      res?.data?.access_token ||
      res?.data?.accessToken;
    let user = normalizeUser(res?.user || res?.data?.user || res?.data);
    if (!user && token) {
      localStorage.setItem('token', token);
      try {
        user = await fetchCurrentUserFromApi();
      } catch (meError) {
        user = null;
      }
    }
    if (!token || !user) {
      throwError('Login response is missing token or user data.');
    }
    const session = { token, user: scrubUser(user) };
    saveSession(session);
    return session;
  } catch (error) {
    throwError(getMessage(error, 'Login failed.'));
  }
}

export async function requestPasswordReset(email) {
  try {
    return await apiFetch('/forgot-password', { method: 'POST', body: { email } });
  } catch (error) {
    throwError(getMessage(error, 'Unable to send reset link.'));
  }
}

export async function resetPassword(token, password) {
  try {
    return await apiFetch('/reset-password', { method: 'POST', body: { token, password } });
  } catch (error) {
    throwError(getMessage(error, 'Unable to reset password.'));
  }
}

export async function changeMemberPassword(payload) {
  try {
    return await changeMemberPasswordApi(payload);
  } catch (error) {
    throwError(getMessage(error, 'Unable to change password.'));
  }
}

export async function getCurrentUser() {
  try {
    return await fetchCurrentUserFromApi();
  } catch (error) {
    throwError(getMessage(error, 'Unable to load current user.'));
  }
}

export function getDefaultMemberProfileSchema() {
  return DEFAULT_MEMBER_PROFILE_SCHEMA.map((field) => ({
    ...field,
    options: Array.isArray(field.options) ? field.options.map(normalizeSchemaOption) : []
  }));
}

export async function getMemberProfileSchema() {
  try {
    const response = await getMemberProfileSchemaApi();
    const root = extractObject(response);
    const fields = Array.isArray(response?.fields)
      ? response.fields
      : Array.isArray(root?.fields)
        ? root.fields
        : extractRows(response);
    const normalized = fields.map(normalizeSchemaField).filter((field) => field.name);
    return normalized.length ? normalized : getDefaultMemberProfileSchema();
  } catch (error) {
    throwError(getMessage(error, 'Unable to load member profile schema.'));
  }
}

export async function getMemberProfile() {
  try {
    const response = await getMemberProfileApi();
    return normalizeMemberProfile(response);
  } catch (error) {
    throwError(getMessage(error, 'Unable to load member profile.'));
  }
}

export async function saveMemberProfile(payload, schema) {
  try {
    return await saveMemberProfileApi(buildProfileFormData(payload, schema));
  } catch (error) {
    throwError(getMessage(error, 'Profile update failed.'));
  }
}

export async function getAdminMemberProfile(memberId) {
  try {
    const response = await getAdminMemberProfileApi(memberId);
    return normalizeMemberProfile(response);
  } catch (error) {
    throwError(getMessage(error, 'Unable to load member profile.'));
  }
}

export async function saveAdminMemberProfile(memberId, payload, schema) {
  try {
    return await saveAdminMemberProfileApi(memberId, buildProfileFormData(payload, schema));
  } catch (error) {
    throwError(getMessage(error, 'Unable to update member profile.'));
  }
}

export async function getAdminView() {
  try {
    const allRes = await getMembersApi();
    const registrations = extractRows(allRes).map(normalizeMemberRecord);

    return {
      registrations,
      pending: [],
      members: registrations
    };
  } catch (error) {
    throwError(getMessage(error, 'Unable to load admin data.'));
  }
}

export async function approveMember(userId, patch = {}) {
  try {
    return await approveMemberApi(userId, patch);
  } catch (error) {
    throwError(getMessage(error, 'Unable to approve member.'));
  }
}

export async function setMemberStatus(userId, active) {
  try {
    if (active) {
      return await activateMemberApi(userId);
    }
    return await deactivateMemberApi(userId);
  } catch (error) {
    throwError(getMessage(error, 'Unable to update member status.'));
  }
}

export function exportMembersCsv(members) {
  const headers = [
    'Full Name',
    'Email',
    'Identity',
    'Articled Period',
    'Phone',
    'City',
    'Country',
    'Profession',
    'Email Verified',
    'Approved',
    'Active'
  ];

  const rows = members.map((m) => [
    m.fullName,
    m.email,
    m.identity,
    m.articledPeriod,
    m.phone,
    m.city,
    m.country,
    m.profession,
    m.emailVerified ? 'Yes' : 'No',
    m.approved ? 'Yes' : 'No',
    m.active ? 'Yes' : 'No'
  ]);

  const csv = [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `alumni-members-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function exportMembersPdf(members) {
  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const rowsHtml = members
    .map(
      (m) => `
      <tr>
        <td>${escapeHtml(m.fullName)}</td>
        <td>${escapeHtml(m.email)}</td>
        <td>${escapeHtml(m.identity)}</td>
        <td>${escapeHtml(m.articledPeriod)}</td>
        <td>${escapeHtml(m.phone)}</td>
        <td>${escapeHtml(m.city)}</td>
        <td>${escapeHtml(m.country)}</td>
        <td>${escapeHtml(m.profession)}</td>
        <td>${m.emailVerified ? 'Yes' : 'No'}</td>
        <td>${m.approved ? 'Yes' : 'No'}</td>
        <td>${m.active ? 'Yes' : 'No'}</td>
      </tr>
    `
    )
    .join('');

  const printWindow = window.open('', '_blank', 'width=1200,height=800');
  if (!printWindow) return;

  printWindow.document.write(`
    <html>
      <head>
        <title>Alumni Members Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 24px; }
          h1 { margin: 0 0 8px; font-size: 22px; }
          p { margin: 0 0 16px; color: #444; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #bbb; padding: 6px 8px; text-align: left; vertical-align: top; }
          th { background: #f0f0f0; }
        </style>
      </head>
      <body>
        <h1>Chatar Alumni Members Report</h1>
        <p>Generated: ${new Date().toLocaleString()}</p>
        <table>
          <thead>
            <tr>
              <th>Full Name</th>
              <th>Email</th>
              <th>Identity</th>
              <th>Articled Period</th>
              <th>Phone</th>
              <th>City</th>
              <th>Country</th>
              <th>Profession</th>
              <th>Email Verified</th>
              <th>Approved</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>
      </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
  }, 250);
}
